import { useCallback, useEffect } from "react";
import {
    GlobalJSBridgePayload,
    GlobalJSBridgeType,
    IPLSwiggySixesDataPayload,
    TimerUpdateSource,
} from "@swiggy-private/cart-types";
// utils
import { isEmpty } from "@swiggy-private/js-utils";
import { isValidNumber } from "@swiggy-private/core-ui";
// hooks
import { useGlobalJSBridgeHandler } from "./useGlobalJSBridgeHandler";

interface UseIPLSwiggySixesJSBridgeProps {
    validateAndSetTimestamp: (inputTimeStampInSec?: number, source?: TimerUpdateSource) => void;
}

export const useIPLSwiggySixesJSBridge = ({ validateAndSetTimestamp }: UseIPLSwiggySixesJSBridgeProps) => {
    const { registerCallbackForType, unregisterCallbackForType } = useGlobalJSBridgeHandler();

    const handleIPLSwiggySixesOfferTimerUpdate = useCallback(
        (payload: GlobalJSBridgePayload) => {
            try {
                const timer = Number((payload?.data as IPLSwiggySixesDataPayload)?.expiry_time_in_sec);
                if (isEmpty(timer) || !isValidNumber(timer)) {
                    return false;
                }
                validateAndSetTimestamp(timer, TimerUpdateSource.APP_JS_BRIDGE);
                return true;
            } catch (err) {
                return false;
            }
        },
        [validateAndSetTimestamp],
    );

    const cleanupAppJSBridgeHandler = useCallback(() => {
        unregisterCallbackForType(GlobalJSBridgeType.IPL_SWIGGY_SIX_OFFER_EXPIRY_TIMER_UPDATE, () => {
            // adding false as default value to give no ack to apps once cleanup is done so they can continue polling
            return false;
        });
    }, [unregisterCallbackForType]);

    useEffect(() => {
        registerCallbackForType(
            GlobalJSBridgeType.IPL_SWIGGY_SIX_OFFER_EXPIRY_TIMER_UPDATE,
            handleIPLSwiggySixesOfferTimerUpdate,
        );
        return () => {
            cleanupAppJSBridgeHandler();
        };
    }, [cleanupAppJSBridgeHandler, handleIPLSwiggySixesOfferTimerUpdate, registerCallbackForType]);

    return {
        cleanupAppJSBridgeHandler,
    };
};
